import { useMemo } from "react";

import { CardProduct } from "@cobasi/ds-components-react";

import { useProductItem } from "@/domain/ProductItem/index";
import { selectItem } from "@/utils/analytics";

import FastBuy from "./FastBuy";

import type {
  ProductItemCardProps,
  ProductItemProps
} from "./productItem.types";
import type { Product, Sku } from "@/domain/ProductItem/productItem.types";

import s from "./productItem.module.scss";

const formatClusterHighlights = (
  clusterHighlights?: Product["clusterHighlights"] | Record<string, string>
) => {
  if (!clusterHighlights) {
    return {};
  }

  if (!Array.isArray(clusterHighlights)) {
    return clusterHighlights;
  }

  return clusterHighlights.reduce<Record<string, string>>((acc, item) => {
    acc[item.id] = item.name;

    return acc;
  }, {});
};

const mapSkuToCardProduct = (sku: Sku) => ({
  bestChoice: sku.bestChoice,
  discountPercent: sku.discountPercent,
  discountValue: sku.discountValue,
  subscriptionPrice: sku.seller.subscriptionPrice,
  sku: sku.id,
  name: sku.name,
  price: sku.seller.price,
  available: sku.seller.status === "AVAILABLE",
  sellerId: sku.seller.id,
  oldPrice: sku.seller.listPrice,
  image: sku.image
});

const getCardClassName = (
  baseClassName: string,
  customClassName?: ProductItemCardProps["className"]
) => {
  return [baseClassName, customClassName].filter(Boolean).join(" ");
};

const mapProductToCardProduct = ({
  product,
  selectedSku,
  href
}: {
  product: Product;
  selectedSku: Sku;
  href?: string;
}) => ({
  id: product.id,
  name: product.name,
  price: selectedSku.seller.price,
  oldPrice: selectedSku.seller.listPrice,
  url: href || product.url,
  status: product.status,
  subscriptionPrice: selectedSku.seller.subscriptionPrice,
  rating: product.rating,
  image: selectedSku.image,
  brand: product.brandName,
  skus: product.skus.map(mapSkuToCardProduct),
  clusterHighlights: formatClusterHighlights(product.clusterHighlights),
  seller: product.skus.map((sku) => ({
    sku: sku.id,
    sellerName: sku.seller.name,
    sellerId: sku.seller.id
  }))
});

const ProductItem = ({
  product,
  withLink,
  href,
  vertical = false,
  cardProps,
  itemListName,
  itemListId,
  position
}: ProductItemProps) => {
  const { isSelectedSkuAvailable, selectedSku, setSelectedSku } =
    useProductItem({
      skus: product.skus,
      clusterHighlights: product.clusterHighlights,
      status: product.status,
      disableClusterHighlights: true
    });

  const cardProduct = useMemo(
    () =>
      mapProductToCardProduct({
        product,
        selectedSku,
        href
      }),
    [product, selectedSku, href]
  );

  const selectedCardSku = useMemo(
    () => mapSkuToCardProduct(selectedSku),
    [selectedSku]
  );

  const showFavorite = cardProps?.showFavorite ?? false;
  const showFastBuy = cardProps?.showFastBuy ?? false;
  const fastBuyWrapperClassName = cardProps?.fastBuyInline
    ? s["fast-buy-inline"]
    : s["fast-buy-absolute"];

  const handleSelectItem = () => {
    selectItem({
      product,
      selectedSkuId: selectedSku.id,
      itemListName,
      itemListId,
      index: position
    });
    cardProps?.onHandleClick?.();
  };

  return (
    <div className={`${s.wrapper} ${vertical ? s.wrapper_vertical : ""}`}>
      <CardProduct
        product={cardProduct}
        chipSelect={selectedCardSku}
        variant={cardProps?.variant ?? "vertical"}
        hasBorder={cardProps?.hasBorder ?? "full"}
        cardSize={cardProps?.cardSize}
        isLinkEnabled={cardProps?.isLinkEnabled ?? withLink}
        isCategory={cardProps?.isCategory ?? false}
        isSynerise={cardProps?.isSynerise ?? false}
        isProductAvailable={isSelectedSkuAvailable}
        isCrossell={cardProps?.isCrossell}
        gaEventFavorite={cardProps?.gaEventFavorite}
        eventLabel={cardProps?.eventLabel}
        itemListId={cardProps?.itemListId}
        darkMode={cardProps?.darkMode}
        $hideListPrice={cardProps?.hideListPrice}
        carouselArrowColor={cardProps?.carouselArrowColor}
        onHandleClick={handleSelectItem}
        skuColor={cardProps?.skuColor}
        onHandleChipSelect={(sku) => {
          const nextSelectedSku = product.skus.find(
            (item) => item.id === sku.sku
          );

          if (nextSelectedSku) {
            setSelectedSku(nextSelectedSku);
          }
        }}
        renderFavorite={showFavorite ? undefined : () => null}
        renderFastBuy={
          showFastBuy
            ? () => (
                <div className={fastBuyWrapperClassName}>
                  <FastBuy
                    isProductAvailable={isSelectedSkuAvailable}
                    selectedSku={selectedSku}
                    fullWidth={cardProps?.fastBuyFullWidth}
                    hideIcon={cardProps?.fastBuyHideIcon}
                    label={cardProps?.fastBuyLabel}
                    buttonVariant={cardProps?.fastBuyButtonVariant}
                    buttonIcon={cardProps?.fastBuyButtonIcon}
                    buttonClassName={cardProps?.fastBuyButtonClassName}
                    inline={cardProps?.fastBuyInline}
                  />
                </div>
              )
            : undefined
        }
        className={getCardClassName(s.card, cardProps?.className)}
      />
    </div>
  );
};
export default ProductItem;
